import React, { useState, useEffect, useRef } from 'react';
import { ParsedVendor, PRICE_RANGE_OPTIONS } from '../data/vendorData';
import Icon from './Icon';
import '../styles/components/DataTable.css';
import '../styles/components/VendorComparison.css';

interface VendorComparisonProps {
  vendors: ParsedVendor[];
  onClose: () => void;
  isPage?: boolean;
}

const VendorComparison: React.FC<VendorComparisonProps> = ({ vendors, onClose, isPage = false }) => {
  const [selectedVendors, setSelectedVendors] = useState<ParsedVendor[]>([]);
  const [searchOpen, setSearchOpen] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    if (searchOpen === null) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSearchOpen(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchOpen]);

  const slots = [0, 1, 2, 3];

  const filteredVendors = vendors.filter(v =>
    !selectedVendors.find(s => s.id === v.id) &&
    v.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectVendor = (slotIdx: number, vendor: ParsedVendor) => {
    const next = [...selectedVendors];
    next[slotIdx] = vendor;
    setSelectedVendors(next.filter(Boolean));
    setSearchOpen(null);
    setSearchTerm('');
  };

  const removeVendor = (slotIdx: number) => {
    const next = [...selectedVendors];
    next.splice(slotIdx, 1);
    setSelectedVendors(next);
  };

  // Was priceRange.length — meant as a rough "higher tier = more ₦ chars"
  // proxy, but it counts characters, not tiers. It broke completely for the
  // free-text ranges some vendors carry ("₦2,550,000 – ₦6,500,000" is 23
  // characters), turning a mid-range vendor into a wildly negative score —
  // and even for glyph tiers it's an accident that ₦/₦₦/₦₦₦/₦₦₦₦ happen to
  // be 1-4 characters long. Indexed against the actual tier list instead;
  // an unrecognized value (free text, "Contact for quote", empty) scores
  // neutral rather than being punished for its string length.
  const getPriceRangeValue = (priceRange: string): number => {
    const tier = PRICE_RANGE_OPTIONS.findIndex(o => o.value === priceRange);
    return tier === -1 ? 0 : tier + 1;
  };

  const getComparisonScore = (vendor: ParsedVendor): number => {
    let score = 0;
    score += vendor.rating * 20;
    score += vendor.verified ? 20 : 0;
    score += vendor.featured ? 10 : 0;
    score += Math.min(vendor.reviewCount / 10, 20);
    score -= getPriceRangeValue(vendor.priceRange) * 5;
    return Math.round(score);
  };

  const body = (
    <>
      {/* 4 fixed placeholder slots */}
      <div className="vc-slots" ref={dropdownRef}>
        {slots.map(idx => {
          const vendor = selectedVendors[idx];
          return (
            <div key={idx} className={`vc-slot${vendor ? ' vc-slot--filled' : ''}`}>
              {vendor ? (
                <>
                  <button className="vc-slot-remove" onClick={() => removeVendor(idx)}>×</button>
                  <div className="vc-slot-name">{vendor.name}</div>
                  <div className="vc-slot-category">
                    {(vendor.services as unknown[])[0]
                      ? (typeof (vendor.services as unknown[])[0] === 'string'
                          ? (vendor.services as string[])[0]
                          : ((vendor.services as { name?: string }[])[0]?.name ?? ''))
                      : ''}
                  </div>
                </>
              ) : (
                <button
                  className="vc-slot-add"
                  onClick={() => { setSearchOpen(idx); setSearchTerm(''); }}
                >
                  <span className="vc-slot-plus">+</span>
                  <span className="vc-slot-label">Add vendor</span>
                </button>
              )}
              {searchOpen === idx && (
                <div className="vc-slot-dropdown">
                  <input
                    autoFocus
                    type="text"
                    className="vc-slot-search"
                    placeholder="Search vendors…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={e => e.key === 'Escape' && setSearchOpen(null)}
                  />
                  <div className="vc-slot-results">
                    {filteredVendors.slice(0, 8).map(v => (
                      <button key={v.id} className="vc-slot-result" onClick={() => selectVendor(idx, v)}>
                        {v.name}
                      </button>
                    ))}
                    {filteredVendors.length === 0 && (
                      <div className="vc-slot-empty">No vendors found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Comparison table — only when at least one vendor selected */}
      {selectedVendors.length > 0 && (
        <div className="comparison-table-wrapper ds-scroll-x">
          <table className="dt vendor-comparison-table">
            <thead>
              <tr>
                <th className="feature-column">Feature</th>
                {selectedVendors.map(vendor => (
                  <th key={vendor.id} className="vendor-column">
                    <div className="vendor-header-cell">
                      <div className="vendor-name">{vendor.name}</div>
                      <div className="comparison-score">
                        Score: {getComparisonScore(vendor)}/100
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="feature-label">Rating</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>
                    <div className="rating-cell">
                      <span className="rating-stars">
                        {Array.from({ length: Math.round(vendor.rating) }, (_, i) => (
                          <Icon key={i} name="star" size={12} />
                        ))}
                      </span>
                      <span className="rating-number">{vendor.rating}/5</span>
                    </div>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="feature-label">Reviews</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>{vendor.reviewCount} reviews</td>
                ))}
              </tr>
              <tr>
                <td className="feature-label">Price Range</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>
                    <span className="price-range">{vendor.priceRange}</span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="feature-label">Services</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>
                    <div className="services-list">
                      {vendor.services.slice(0, 3).map((service: unknown, idx) => (
                        <span key={idx} className="service-tag">
                          {typeof service === 'string' ? service : (service as { name?: string })?.name ?? String(service ?? '')}
                        </span>
                      ))}
                      {vendor.services.length > 3 && (
                        <span className="service-tag">+{vendor.services.length - 3} more</span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="feature-label">Specializations</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>
                    {(vendor.specializations as unknown[])
                      .map(s => typeof s === 'string' ? s : (s as { name?: string })?.name ?? String(s ?? ''))
                      .join(', ')}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="feature-label">Verified</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>
                    {vendor.verified ? (
                      <span className="vc-badge verified">✓ Verified</span>
                    ) : (
                      <span className="vc-badge not-verified">Not Verified</span>
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="feature-label">Featured</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>
                    {vendor.featured ? (
                      <span className="vc-badge featured">
                        <Icon name="star" size={10} /> Featured
                      </span>
                    ) : (
                      <span className="vc-badge">—</span>
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="feature-label">Availability</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>
                    {Array.isArray(vendor.availability)
                      ? vendor.availability.join(', ')
                      : vendor.availability ? 'Available' : 'Not Available'}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="feature-label">Contact</td>
                {selectedVendors.map(vendor => (
                  <td key={vendor.id}>
                    <div className="contact-links">
                      {vendor.contact.instagram && (
                        <a href={vendor.contact.instagram} target="_blank" rel="noopener noreferrer">
                          Instagram
                        </a>
                      )}
                      {vendor.contact.website && (
                        <a href={vendor.contact.website} target="_blank" rel="noopener noreferrer">
                          Website
                        </a>
                      )}
                      {vendor.contact.email && (
                        <a href={`mailto:${vendor.contact.email}`}>Email</a>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  if (isPage) {
    return (
      <div className="vendor-comparison-page">
        <div className="comparison-container">
          {body}
        </div>
      </div>
    );
  }

  return (
    <div
      className="vnd-cmp-backdrop"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="vendor-comparison-modal">
        <div className="comparison-container">
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
          {body}
        </div>
      </div>
    </div>
  );
};

export default VendorComparison;
